import { VariableType } from 'json-to-graphql-query';
import type { FieldValue } from './types';

interface MetadataFieldNodeResult {
  id: FieldValue;
  definition:
    | {
        name: FieldValue;
      }
    | false;
  values:
    | {
        edges: {
          node: {
            id: FieldValue;
            data: {
              data: FieldValue;
            };
          };
        };
      }
    | false;
}

interface MetaResult {
  id: FieldValue;
  definition:
    | {
        name: FieldValue;
        slug: FieldValue;
      }
    | false;
  values:
    | {
        id: FieldValue;
        data: {
          data: FieldValue;
        };
      }
    | false;
}

interface AllLibMetadataFieldsQueryResult {
  query: {
    __variables: {
      globalId: 'String';
      definition_Name_In: '[String]';
    };
    allLibMetadataFields: {
      totalCount: FieldValue;
      __args: {
        globalId: VariableType;
        definition_Name_In: VariableType;
      };
      edges: {
        node: MetadataFieldNodeResult;
      };
    };
  };
}

interface MetaQueryResult {
  query: {
    __variables: {
      definitionSlugIn: '[String]';
      globalId: 'ID';
      itemId: 'String';
    };
    meta: MetaResult & {
      __args: {
        globalId: VariableType;
        definitionSlugIn: VariableType;
        itemId: VariableType;
      };
    };
  };
}

interface LibMetadataFieldResult {
  id: FieldValue;
  definition: {
    id: FieldValue;
    name: FieldValue;
    fieldType: FieldValue;
  };
  values: {
    edges: {
      node: {
        id: FieldValue;
        data: FieldValue | { data: FieldValue };
      };
    };
  };
}

interface UpdateMetadataMutationResult {
  mutation: {
    __variables: {
      fieldId: 'ID!';
      valuesList: '[String]';
    };
    updateLibMetadataField: {
      __args: {
        fieldId: VariableType;
        valuesList: VariableType;
      };
      libMetadataField: LibMetadataFieldResult;
    };
  };
}

interface CreateUpdateMetadataMutationResult {
  mutation: {
    __variables: {
      globalId: 'ID!';
      fieldDefId: 'ID';
      fieldId: 'ID';
      valuesList: '[String]';
    };
    createUpdateLibMetadataField: {
      __args: {
        globalId: VariableType;
        fieldDefId: VariableType;
        fieldId: VariableType;
        valuesList: VariableType;
      };
      libMetadataField: {
        id: FieldValue;
        definition: {
          id: FieldValue;
          name: FieldValue;
          fieldType: FieldValue;
        };
        values: {
          edges: {
            node: {
              id: FieldValue;
              data: {
                data: FieldValue;
              };
            };
          };
        };
      };
    };
  };
}

export const MetadataFieldNode = (truify: boolean = false): MetadataFieldNodeResult => ({
  id: truify,
  definition: truify
    ? {
        name: truify,
      }
    : false,
  values: truify
    ? {
        edges: {
          node: {
            id: truify,
            data: {
              data: truify,
            },
          },
        },
      }
    : false,
});

export const Meta = (truify: boolean = false): MetaResult => ({
  id: truify,
  definition: truify
    ? {
        name: truify,
        slug: truify,
      }
    : false,
  values: truify
    ? {
        id: truify,
        data: {
          data: truify,
        },
      }
    : false,
});

export const allLibMetadataFieldsQuery = (truify: boolean = false): AllLibMetadataFieldsQueryResult => ({
  query: {
    __variables: {
      globalId: 'String',
      definition_Name_In: '[String]',
    },
    allLibMetadataFields: {
      totalCount: truify,
      __args: {
        globalId: new VariableType('globalId'),
        definition_Name_In: new VariableType('definition_Name_In'),
      },
      edges: {
        node: { ...MetadataFieldNode(truify) },
      },
    },
  },
});

export const MetaQ = (truify: boolean = false): MetaQueryResult => ({
  query: {
    __variables: {
      definitionSlugIn: '[String]',
      globalId: 'ID',
      itemId: 'String',
    },
    meta: {
      __args: {
        globalId: new VariableType('globalId'),
        definitionSlugIn: new VariableType('definitionSlugIn'),
        itemId: new VariableType('itemId'),
      },
      ...Meta(truify),
    },
  },
});

export const updateMetadataMutation = (truify: boolean = true): UpdateMetadataMutationResult => ({
  mutation: {
    __variables: {
      fieldId: 'ID!',
      valuesList: '[String]',
    },
    updateLibMetadataField: {
      __args: {
        fieldId: new VariableType('fieldId'),
        valuesList: new VariableType('valuesList'),
      },
      libMetadataField: {
        id: truify,
        definition: {
          id: truify,
          name: truify,
          fieldType: truify,
        },
        values: {
          edges: {
            node: {
              id: truify,
              data: truify,
            },
          },
        },
      },
    },
  },
});

export const createUpdateMetadataMutation = (truify: boolean = true): CreateUpdateMetadataMutationResult => ({
  mutation: {
    __variables: {
      globalId: 'ID!',
      fieldDefId: 'ID',
      fieldId: 'ID',
      valuesList: '[String]',
    },
    createUpdateLibMetadataField: {
      __args: {
        globalId: new VariableType('globalId'),
        fieldDefId: new VariableType('fieldDefId'),
        fieldId: new VariableType('fieldId'),
        valuesList: new VariableType('valuesList'),
      },
      libMetadataField: {
        id: truify,
        definition: {
          id: truify,
          name: truify,
          fieldType: truify,
        },
        values: {
          edges: {
            node: {
              id: truify,
              data: {
                data: truify,
              },
            },
          },
        },
      },
    },
  },
});
